// Generated from contracts/product-revival/v1/openapi.json v1.0.0.
// Do not edit by hand.

import type {
  InvoicePrintDocument,
  InvoicePrintFormat,
  InvoiceRenderingMetadata,
} from "./invoice-print";
import { parseStableApiError, StableApiError } from "./contracts";

export type JsonObject = Record<string, unknown>;
export type RevivalHttpMethod = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
export type RevivalResponseType = "json" | "blob" | "text";

export interface RevivalRequest {
  path: string;
  method: RevivalHttpMethod;
  headers?: Record<string, string>;
  query?: Record<string, string | number | boolean | null | undefined>;
  body?: unknown;
  responseType?: RevivalResponseType;
  timeoutMs?: number;
}

export type RevivalTransport = <T>(request: RevivalRequest) => Promise<T>;

export interface RevivalCommandOptions {
  idempotencyKey: string;
  clientVersion?: string;
  clientApplication?: "desktop" | "web" | "mobile" | "admin";
  correlationId?: string;
  businessDate?: string;
  sourceDevice?: string;
  periodTreatment?: "ORIGINAL_PERIOD" | "NEXT_OPEN_PERIOD";
  periodReason?: string;
}

export interface RevivalHealthResponse {
  status: string;
  timestamp?: string;
  [key: string]: unknown;
}

export type ImportStatus =
  | "UPLOADED"
  | "PARSING"
  | "MAPPING"
  | "VALIDATING"
  | "READY_TO_COMMIT"
  | "COMMITTING"
  | "COMMITTED"
  | "RECONCILED"
  | "ACTIVATED"
  | "FAILED"
  | "REVERSING"
  | "REVERSED";

export type ImportDisposition =
  | "PENDING"
  | "ACCEPTED"
  | "REJECTED"
  | "IGNORED"
  | "MERGED";

export interface ImportFileMetadata {
  id: string;
  checksumSha256: string;
  originalFilename: string;
  byteSize: string;
  mimeType: string;
  detectedFormat: "CSV" | "TSV" | "XLSX" | "HTML_XLS";
  encoding?: string | null;
  delimiter?: string | null;
}

export interface ImportErrorRecord {
  id: string;
  rowId?: string | null;
  code: string;
  field?: string | null;
  message: string;
  severity: "WARNING" | "ERROR" | "FATAL";
  rawValue?: string | null;
  row?: {
    sourceSheet?: string | null;
    sourceRow: number;
    sourceCoordinates: string;
  } | null;
}

export interface ImportRowRecord {
  id: string;
  sourceSheet?: string | null;
  sourceRow: number;
  sourceCoordinates: string;
  rawRecord: JsonObject;
  normalizedRecord?: JsonObject | null;
  disposition: ImportDisposition;
  dispositionReason?: string | null;
  catalogDisposition:
    | "PENDING"
    | "ELIGIBLE"
    | "REVIEW_REQUIRED"
    | "MERGED"
    | "REJECTED"
    | "IMPORTED";
  catalogReason?: string | null;
  inventoryDisposition:
    | "PENDING"
    | "AVAILABLE"
    | "EXPIRED"
    | "ZERO"
    | "REJECTED";
  inventoryReason?: string | null;
  inventoryBucket?: "available" | "expired" | null;
  entityType?: string | null;
  entityId?: string | null;
}

export interface ImportJobRecord {
  id: string;
  fileId: string;
  profileId?: string | null;
  status: ImportStatus;
  mappingVersion?: number | null;
  totalRows: number;
  acceptedRows: number;
  rejectedRows: number;
  ignoredRows: number;
  file: ImportFileMetadata;
  rows: ImportRowRecord[];
  errors: ImportErrorRecord[];
  reconciliation?: { approvedAt?: string | null } | null;
}

export interface MappingSuggestion {
  sourceHeader: string;
  targetField: string | null;
  confidenceBps: number;
  requiresReview: boolean;
  alternatives: Array<{ target: string; scoreBps: number }>;
}

export interface MappingInference {
  mapping: Record<string, string | null>;
  suggestions: MappingSuggestion[];
  coverageBps: number;
  confidenceBps: number;
  schemaFingerprint?: string;
  matchedTemplate?: {
    id: string;
    name: string;
    sourceSystem: string;
    version: number;
    confidenceBps: number;
    assumptions: string[];
  } | null;
  normalization?: JsonObject | null;
}

export interface MergeProposal {
  rowId: string;
  sourceRow: number;
  sourceCoordinates: string;
  disposition: ImportDisposition;
  catalogDisposition: ImportRowRecord["catalogDisposition"];
  catalogReason?: string | null;
  inventoryDisposition: ImportRowRecord["inventoryDisposition"];
  inventoryReason?: string | null;
  inventoryBucket?: "available" | "expired" | null;
  proposedAction:
    | "CREATE"
    | "MERGE_PROPOSED"
    | "MERGE_AUTO"
    | "MERGE"
    | "REJECT"
    | "IGNORE";
  confidenceBps: number | null;
  requiresDecision: boolean;
  product: {
    id?: string;
    name?: string;
    sku?: string;
    create?: { name?: string; sku?: string };
  };
  supplier?: { sourceName: string; action: string } | null;
  pack?: { action: string; code?: string; id?: string } | null;
  batch?: { action: string; number?: string; id?: string } | null;
  validationErrors: ImportErrorRecord[];
}

export interface ImportPreview {
  jobId: string;
  status: ImportStatus;
  requiresDecision: number;
  totals: Record<string, number>;
  proposals: MergeProposal[];
}

export interface ImportReconciliation {
  id: string;
  jobId: string;
  approved: boolean;
  requiresDecision: number;
  rows: {
    source: number;
    accepted: number;
    rejected: number;
    ignored: number;
  };
  products: { source: number; proposedLive: number };
  batches: { source: number; proposedLive: number };
  quantity: {
    source: string;
    proposedLive: string;
    expiredTracked: string;
    rejected: string;
    ignored: string;
    variance: string;
  };
  valuePaise: { source: string; proposedLive: string; variance: string };
}

export interface ImportCommandResult {
  jobId: string;
  status: ImportStatus;
  importedProducts?: number;
  importedLots?: number;
  importedQuantity?: string;
  importedExpiredQuantity?: string;
  reversedMovements?: number;
  mode?: string;
  replayed: boolean;
}

export interface ImportProfileRecord {
  id: string;
  name: string;
  sourceSystem?: string | null;
  mapping: Record<string, string | null>;
  normalization?: JsonObject | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface SaveImportProfileRequest {
  name: string;
  sourceSystem?: string;
  mapping: Record<string, string | null>;
  normalization?: JsonObject;
}

export interface ApplyImportProfileRequest {
  profileId: string;
}

export interface ImportRowDecision {
  rowId: string;
  disposition: ImportDisposition;
  entityId?: string;
  reason?: string;
}

export interface ApplyImportDecisionsRequest {
  decisions: ImportRowDecision[];
}

export interface CorrectImportRowRequest {
  values: JsonObject;
}

export interface ReconcileImportRequest {
  approve?: boolean;
  reason?: string;
  approvalRequestId?: string;
}

export interface UndoImportRequest {
  reason: string;
}

export interface ImportErrorFilters {
  code?: string;
  field?: string;
  severity?: string;
  sourceRow?: number;
}

export interface ReadinessEvidence {
  source: string;
  entityId?: string;
  recordedAt?: string | null;
  detail: string;
}

export interface ReadinessStep {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  completedAt: string | null;
  route: string;
  priority: number;
  evidence: ReadinessEvidence[];
}

export interface ReadinessProgress {
  tenantId: string;
  storeId: string;
  completedSteps: number;
  totalSteps: number;
  percentComplete: number;
  isComplete: boolean;
  checklist: ReadinessStep[];
}

export interface OnboardingStatus {
  dismissed: boolean;
  isComplete: boolean;
  shouldShow: boolean;
  progress: ReadinessProgress;
}

export interface CreateImportSupportAccessRequest {
  tenantId: string;
  storeId: string;
  reason: string;
  durationMinutes: number;
}

export interface ImportSupportAccess {
  id: string;
  adminId?: string;
  tenantId: string;
  storeId: string;
  reason: string;
  createdAt: string;
  expiresAt: string;
  endedAt?: string | null;
}

export interface ImportSupportFile {
  originalFilename: string;
  checksumSha256: string;
  detectedFormat?: string | null;
  createdAt: string;
}

export interface ImportSupportJob {
  id: string;
  status: string;
  rowCount: number;
  acceptedRows: number;
  rejectedRows: number;
  ignoredRows: number;
  createdAt: string;
  updatedAt: string;
  file: ImportSupportFile;
  reconciliation?: {
    approvedAt?: string | null;
    reconciledAt?: string | null;
  } | null;
  _count: { errors: number; rows: number };
}

export interface ImportSupportJobDetails {
  access: ImportSupportAccess;
  job: ImportSupportJob & {
    file: ImportSupportFile & { id: string; byteSize: number | string };
    reconciliation?: JsonObject | null;
    errors: Array<{
      id: string;
      code: string;
      message: string;
      severity: string;
      field?: string | null;
      createdAt: string;
      row?: {
        sourceCoordinates?: JsonObject | string | null;
        sourceRow?: number | null;
      } | null;
    }>;
  };
  audit: Array<{
    id: string;
    action: string;
    actorId?: string | null;
    payload?: JsonObject | null;
    createdAt: string;
  }>;
  outbox: Array<{
    id: string;
    eventType: string;
    status: string;
    attempts: number;
    lastError?: string | null;
    createdAt: string;
  }>;
  supportActions: Array<{
    id: string;
    action: string;
    status: string;
    error?: string | null;
    createdAt: string;
    completedAt?: string | null;
  }>;
}

export interface ImportSupportJobsQuery {
  accessId: string;
  status?: string;
  jobId?: string;
}

export interface ImportSupportJobsResponse {
  access: ImportSupportAccess;
  jobs: ImportSupportJob[];
}

export interface RetryImportSupportJobRequest {
  accessId: string;
  reason: string;
}

export interface RetryImportSupportJobResponse {
  jobId: string;
  status: string;
  acceptedRows: number;
  rejectedRows: number;
  replayed: boolean;
}

export interface SaleQuoteItemRequest {
  lineId: string;
  productId: string;
  packId?: string;
  quantity: string;
  freeQuantity?: string;
  replacementQuantity?: string;
  sampleQuantity?: string;
  discountBps?: number;
  priceBasis?: "BASE_UNIT" | "SELECTED_PACK";
  taxBasis?: "INCLUSIVE" | "EXCLUSIVE";
  preferredLotId?: string;
  lotOverrideReason?: string;
  prescribedProductId?: string;
  prescriptionId?: string;
  prescriptionLineId?: string;
  substitutionReason?: string;
  substitutionConsent?: boolean;
  minimumResidualShelfLifeDays?: number;
  coldChainRequired?: boolean;
  controlledStorageRequired?: boolean;
}

export interface SaleQuoteChargeRequest {
  lineId: string;
  chargeCode: string;
  quantity: string;
  discountBps?: number;
}

export interface SaleQuoteKitRequest {
  lineId: string;
  kitCode: string;
  quantity: string;
  discountBps?: number;
}

export interface CreateSaleQuoteRequest {
  customerId?: string;
  interstate: boolean;
  billDiscountPaise?: number;
  context?: JsonObject;
  items: SaleQuoteItemRequest[];
  charges?: SaleQuoteChargeRequest[];
  kits?: SaleQuoteKitRequest[];
}

export interface RequoteSaleQuoteRequest {
  quoteVersion: number;
}

export interface CancelSaleQuoteRequest {
  expectedVersion: number;
  reason: string;
}

export interface SaleWorkflowRequest {
  kind:
    | "CART"
    | "ESTIMATE"
    | "ORDER"
    | "DELIVERY"
    | "CORRECTION"
    | "EXCHANGE"
    | "WHOLESALE_CONTRACT"
    | "RAPID_MASTER";
  quoteId?: string;
  saleId?: string;
  parentWorkflowId?: string;
  channel?: string;
  externalIdentity?: string;
  counterId?: string;
  assignedActorId?: string;
  approvalRequestId?: string;
  payload: JsonObject;
  expiresAt?: string;
}

export interface SaleWorkflowTransitionRequest {
  expectedVersion: number;
  toState: string;
  reason?: string;
  payload?: JsonObject;
  quoteId?: string;
  saleId?: string;
  saleReturnId?: string;
  counterId?: string;
}

export interface SaleCartEditRequest {
  expectedVersion: number;
  action:
    | "SCAN_ADD"
    | "UPDATE_LINE"
    | "UNDO_SCAN"
    | "REMOVE_LINE"
    | "CLEAR_CART"
    | "TRANSFER_COUNTER"
    | "ABANDON_PAYMENT";
  line?: JsonObject;
  lineId?: string;
  counterId?: string;
  reason?: string;
  approvalRequestId?: string;
}

export interface SaleWorkflowQuery {
  kind?: SaleWorkflowRequest["kind"];
  state?: string;
  externalIdentity?: string;
}

export interface SalesLookupQuery {
  invoiceNumber?: string;
  customerId?: string;
  patientId?: string;
  productId?: string;
  batchNumber?: string;
  tenderMethod?: string;
  prescriptionId?: string;
  barcodeOrQr?: string;
  from?: string;
  to?: string;
  network?: boolean;
  page?: number;
  limit?: number;
}

export interface SaleQuoteResponse extends JsonObject {
  quoteId: string;
  version: number;
  tenantId: string;
  storeId: string;
  createdAt: string;
  expiresAt: string;
  customerId: string | null;
  interstate: boolean;
  lines: SaleQuoteLineResponse[];
  subtotalPaise: number;
  discountPaise: number;
  taxablePaise: number;
  taxPaise: number;
  roundOffPaise: number;
  payablePaise: number;
  allowedTenders: Array<"CASH" | "UPI" | "CARD" | "LOYALTY" | "ADVANCE">;
  requoteOf?: {
    quoteId: string;
    version: number;
    requestHash: string;
  };
  requote?: {
    replacedQuoteId: string;
    replacedVersion: number;
    changes: Record<string, { before: number; after: number }>;
    lineChanges: Array<{
      lineId: string;
      productId: string;
      changedFields: string[];
      fields: Record<string, { before: number | null; after: number }>;
      lots: {
        before: Array<{
          lotId: string;
          quantity: string;
          balanceVersion: number;
        }>;
        after: Array<{
          lotId: string;
          quantity: string;
          balanceVersion: number;
        }>;
      };
    }>;
  };
}

export interface SaleQuoteLineResponse extends JsonObject {
  lineId: string;
  productId: string;
  productName: string;
  storeProductVersion: number;
  quantity: string;
  freeQuantity: string;
  replacementQuantity: string;
  sampleQuantity: string;
  uomSnapshot: JsonObject;
  priceSnapshot: JsonObject;
  discountSnapshot: JsonObject;
  unitMrpPaise: number;
  unitPricePaise: number;
  gstBps: number;
  discountBps: number;
  grossPaise: number;
  discountPaise: number;
  taxablePaise: number;
  cgstPaise: number;
  sgstPaise: number;
  igstPaise: number;
  taxPaise: number;
  totalPaise: number;
  complianceFields: string[];
  warnings?: string[];
  lots: Array<{
    lotId: string;
    batchNumber: string;
    quantity: string;
    balanceVersion: number;
    expiryDate?: string | null;
  }>;
}

export interface SaleTenderRequest {
  method:
    | "CASH"
    | "UPI"
    | "CARD"
    | "BANK_TRANSFER"
    | "CHEQUE"
    | "LOYALTY"
    | "ADVANCE"
    | "GIFT_VOUCHER"
    | "STORE_WALLET"
    | "PROMOTIONAL_CREDIT";
  amountPaise: number;
  tenderedPaise?: number;
  reference?: string;
  paymentAttemptId?: string;
}

export interface CreateCanonicalPaymentAttemptRequest {
  saleId?: string;
  quoteId?: string;
  billingSessionId?: string;
  customerId?: string;
  method: SaleTenderRequest["method"] | "CUSTOMER_CREDIT" | "COD";
  amountPaise: number;
  appliedPaise?: number;
  tenderedPaise?: number;
  reference?: string;
  provider?: string;
  expiresAt?: string;
  metadata?: JsonObject;
}

export interface CanonicalPaymentAttemptResponse extends JsonObject {
  id: string;
  tenantId: string;
  storeId: string;
  saleId: string | null;
  quoteId: string | null;
  billingSessionId: string | null;
  customerId: string | null;
  method: CreateCanonicalPaymentAttemptRequest["method"];
  status:
    | "PENDING"
    | "AUTHORIZED"
    | "SUCCESS"
    | "FAILED"
    | "TIMED_OUT"
    | "SETTLED"
    | "VOIDED"
    | "REVERSED"
    | "REFUNDED"
    | "CHARGEBACK"
    | "CLEARED"
    | "BOUNCED"
    | "EXCESS_COLLECTION";
  amountPaise: string;
  tenderedPaise: string | null;
  appliedPaise: string;
  changePaise: string;
  actualChangePaise: string;
  changeVariancePaise: string;
  provider: string | null;
  providerReference: string | null;
  providerOrderReference: string | null;
  providerPaymentReference: string | null;
  metadata: JsonObject;
  expiresAt?: string | null;
  createdAt: string;
}

export interface AcceptSaleRequest {
  quoteId: string;
  quoteVersion: number;
  counterId: string;
  billingSessionId: string;
  tenders: SaleTenderRequest[];
  zeroPayReason?: "BILL_OF_SUPPLY" | "FREE_REPLACEMENT";
  allowPartial?: boolean;
  compliance?: Record<string, string>;
  regulatedDispense?: {
    approvalRequestId?: string;
    emergency?: Record<string, unknown>;
    storageLocationId?: string;
  };
  workflowId?: string;
  workflowExpectedVersion?: number;
  workflowToState?: string;
  workflowFulfilments?: Array<{
    workflowLineId: string;
    quoteLineId: string;
    quantity: string;
  }>;
  print?: boolean;
  documentType?: "TAX_INVOICE" | "BILL_OF_SUPPLY";
}

export interface AcceptSaleResponse extends JsonObject {
  saleId: string;
  invoiceNumber: string;
  quoteId: string;
  payablePaise: number;
  acceptedAt: string;
  replayed: boolean;
}

export interface OfflineSnapshotQuery {
  cursor?: string;
  pageSize?: number;
}

export interface OfflineSnapshotProduct {
  productId: string;
  name: string;
  sku: string;
  manufacturer: string | null;
  hsnCode: string | null;
  scheduleType: string;
  unit: string;
  sellingPricePaise: number;
  mrpPaise: number;
  gstBps: number;
  storeProductVersion: number;
  updatedAt: string;
}

export interface OfflineSnapshotIdentifier {
  productId: string;
  identifierType: string;
  normalizedValue: string;
}

export interface OfflineSnapshotLot {
  lotId: string;
  productId: string;
  batchNumber: string;
  expiryDate: string | null;
  status: string;
  available: string;
  reserved: string;
  damaged: string;
  balanceVersion: number;
  costPaise: number;
  mrpPaise: number;
  updatedAt: string;
}

export interface OfflineSnapshotTombstone {
  entityType: "PRODUCT" | "IDENTIFIER" | "LOT";
  entityId: string;
}

export interface OfflineSnapshotResponse {
  schemaVersion: number;
  mode: "FULL" | "DELTA";
  resetRequired: boolean;
  snapshotAt: string;
  cursor: string;
  hasMore: boolean;
  products: OfflineSnapshotProduct[];
  identifiers: OfflineSnapshotIdentifier[];
  lots: OfflineSnapshotLot[];
  tombstones: OfflineSnapshotTombstone[];
}

export interface AcceptOfflineSaleRequest {
  clientCommandId: string;
  provisionalReceiptNumber: string;
  snapshotCursor: string;
  quoteSnapshot: SaleQuoteResponse;
  customerId?: string;
  interstate: false;
  items: SaleQuoteItemRequest[];
  counterId: string;
  billingSessionId: string;
  tenders: SaleTenderRequest[];
  print?: boolean;
}

export interface OfflineSaleResolutionRequest {
  action: "REQUOTE" | "REFUND";
  replacementQuoteId?: string;
  saleReturnId?: string;
  reason: string;
}

export interface CounterSession {
  id: string;
  sessionNumber: string;
  counterId: string;
  counterName: string;
  counterCode: string | null;
  shift: "MORNING" | "AFTERNOON" | "EVENING" | "NIGHT";
  status: "OPEN" | "CLOSED" | "RECONCILED";
  startTime: string;
  endTime: string | null;
  openingCashPaise: number;
  invoiceCount: number;
}

export interface CounterSessionWorkspace {
  counters: Array<{
    id: string;
    name: string;
    code: string | null;
    activeSession: CounterSession | null;
  }>;
  selectedCounterId: string | null;
  activeSession: CounterSession | null;
  autoSelected: boolean;
  requiresCounterSelection: boolean;
  pendingServerCommands: number;
}

export interface OpenCounterSessionRequest {
  counterId?: string;
  shift: "MORNING" | "AFTERNOON" | "EVENING" | "NIGHT";
  openingCashPaise: number;
}

export interface CounterTenderAmounts {
  CASH: number;
  CARD: number;
  UPI: number;
}

export interface OpenCounterSessionResponse extends JsonObject {
  session: CounterSession;
  selectedCounterId: string;
  openingTender: CounterTenderAmounts;
  replayed: boolean;
}

export interface CounterSessionClosePreview {
  session: CounterSession;
  expected: CounterTenderAmounts;
  pendingServerCommands: number;
  closeAllowedByServer: boolean;
}

export interface CloseCounterSessionRequest {
  cashPaise: number;
  cardPaise: number;
  upiPaise: number;
  connectivity: "ONLINE" | "OFFLINE";
  pendingClientCommands: number;
  notes?: string;
}

export interface CloseCounterSessionResponse extends JsonObject {
  session: CounterSession;
  expected: CounterTenderAmounts;
  actual: CounterTenderAmounts;
  variance: CounterTenderAmounts;
  replayed: boolean;
}

export interface ConfirmPrinterTestRequest {
  testReference: string;
  testedAt: string;
  status?: "SUCCEEDED" | "FAILED";
  failureMessage?: string;
}

export interface PrinterTestResponse {
  printerId: string;
  paperWidth: number;
  purpose: "BILLING" | "LABEL" | "REPORT";
  status: "SUCCEEDED" | "FAILED";
  testedAt: string;
  testReference: string | null;
}

export interface InvoicePrintQuery {
  format?: InvoicePrintFormat;
  printerId?: string;
}

export interface InvoicePrintResponse {
  format: InvoicePrintFormat;
  rendering: InvoiceRenderingMetadata;
  document: InvoicePrintDocument;
}

export interface ReturnableSaleQuery {
  invoiceNumber: string;
  includeMigrated?: boolean;
}

export interface ReturnableSaleLot {
  originalSaleLineId: string;
  productId: string;
  productName: string;
  sku: string;
  unit: string;
  lotId: string;
  batchNumber: string;
  expiryDate: string | null;
  soldQuantity: string;
  returnedQuantity: string;
  returnableQuantity: string;
  originalLineQuantity: string;
  originalFreeQuantity: string;
  originalPromotionalFreeQuantity?: string;
  originalReplacementQuantity?: string;
  originalSampleQuantity?: string;
  returnedPromotionalFreeQuantity?: string;
  returnedReplacementQuantity?: string;
  returnedSampleQuantity?: string;
  soldPaidQuantity?: string;
  soldFreeQuantity?: string;
  returnedPaidQuantity?: string;
  returnedFreeQuantity?: string;
  returnablePaidQuantity?: string;
  returnableFreeQuantity?: string;
  lineTotalPaise: number;
  settlementAdjustmentPaise?: number;
  refundablePaise: number;
}

export interface ReturnableSaleCharge {
  originalChargeLineId: string;
  chargeCode: string;
  chargeName: string;
  lineKind: "DELIVERY" | "PACKING" | "HANDLING" | "OTHER_SERVICE";
  uqc: string;
  soldQuantity: string;
  returnedQuantity: string;
  returnableQuantity: string;
  refundable: boolean;
  refundPolicy: JsonObject;
  unitPricePaise: number;
  gstBps: number;
  lineTotalPaise: number;
  settlementAdjustmentPaise: number;
  refundablePaise: number;
}

export interface ReturnableSaleKitComponentLot {
  lotId: string;
  batchNumber: string;
  expiryDate: string | null;
  soldQuantity: string;
  returnedQuantity: string;
  returnableQuantity: string;
}

export interface ReturnableSaleKitComponent {
  originalComponentId: string;
  productId: string;
  productName: string;
  soldQuantity: string;
  returnedQuantity: string;
  returnableQuantity: string;
  returnable: boolean;
  taxAllocationBps: number;
  refundablePaise: number;
  saleableFormSnapshot: JsonObject;
  lots: ReturnableSaleKitComponentLot[];
}

export interface ReturnableSaleKit {
  originalKitLineId: string;
  kitCode: string;
  kitName: string;
  packIdentity: string;
  kitBatchNumber: string;
  kitExpiryDate: string;
  uqc: string;
  soldQuantity: string;
  returnedQuantity: string;
  returnableQuantity: string;
  refundable: boolean;
  returnMode: "FULL_KIT_ONLY" | "COMPONENT" | "NON_RETURNABLE";
  returnPolicy: JsonObject;
  unitPricePaise: number;
  gstBps: number;
  lineTotalPaise: number;
  settlementAdjustmentPaise: number;
  refundablePaise: number;
  components: ReturnableSaleKitComponent[];
}

export interface ReturnableSaleResponse {
  source: "CANONICAL" | "MIGRATED_LEGACY";
  saleId: string;
  invoiceNumber: string;
  status: string;
  acceptedAt: string;
  originalStoreId: string;
  crossStore: boolean;
  ageDays: number;
  returnPolicy: {
    returnWindowDays: number;
    allowWithoutReceipt: boolean;
    requireManagerForWithoutReceipt: boolean;
    requireInspectionAbovePaise: number;
    freeUnitRefund: "ZERO" | "REPLACEMENT_ONLY";
  };
  eligibility: {
    ageDays: number;
    withinWindow: boolean;
    managerOverrideRequired: boolean;
    receiptlessAllowed: boolean;
    requiresCanonicalization: boolean;
  };
  requiresCanonicalization?: boolean;
  payablePaise: number;
  customer: { id: string; name: string; phone?: string | null } | null;
  originalTenders: Array<{
    method:
      | "CASH"
      | "UPI"
      | "CARD"
      | "LOYALTY"
      | "CREDIT"
      | "CHEQUE"
      | "BANK_TRANSFER"
      | "MIXED";
    amountPaise: number;
    reference?: string | null;
  }>;
  lots: ReturnableSaleLot[];
  charges: ReturnableSaleCharge[];
  kits: ReturnableSaleKit[];
}

export interface SaleReturnLineRequest {
  originalSaleLineId: string;
  lotId: string;
  quantity: string;
  reason?: string;
  paidQuantity?: string;
  freeQuantity?: string;
  promotionalFreeQuantity?: string;
  replacementQuantity?: string;
  sampleQuantity?: string;
  condition?: string;
  disposition?: string;
  regulatedReturn?: {
    approvalRequestId?: string;
    storageLocationId?: string;
  };
}

export interface SaleReturnChargeRequest {
  originalChargeLineId: string;
  quantity: string;
  reason?: string;
}

export interface SaleReturnKitComponentRequest {
  originalComponentId: string;
  lotId: string;
  quantity: string;
  condition?: string;
  disposition?: string;
}

export interface SaleReturnKitRequest {
  originalKitLineId: string;
  quantity: string;
  reason?: string;
  components: SaleReturnKitComponentRequest[];
}

export interface RefundAllocationRequest {
  method: "CASH" | "UPI" | "CARD" | "LOYALTY" | "CREDIT";
  amountPaise: number;
  reference?: string;
}

export interface CreateSaleReturnRequest {
  originalSaleId: string;
  reason: string;
  lines: SaleReturnLineRequest[];
  charges?: SaleReturnChargeRequest[];
  kits?: SaleReturnKitRequest[];
  refunds: RefundAllocationRequest[];
  refundSessionId?: string;
  returnType?: string;
  receivingStoreId?: string;
  managerApprovalRequestId?: string;
  inspectionRequired?: boolean;
  inspectionCaseId?: string;
  exchangeSaleId?: string;
  recallControlId?: string;
  supplierClaimRef?: string;
}

export interface CancelPostedSaleRequest {
  reason: string;
  cancellationMode: "NOT_SUPPLIED";
  refunds: RefundAllocationRequest[];
  condition?:
    | "SEALED_SALEABLE"
    | "OPENED"
    | "DAMAGED"
    | "EXPIRED"
    | "RECALLED"
    | "COLD_CHAIN_UNVERIFIED";
  disposition?:
    | "AVAILABLE"
    | "QUARANTINE"
    | "DAMAGED"
    | "EXPIRED"
    | "RECALLED"
    | "SUPPLIER_CLAIM";
  refundSessionId?: string;
  managerApprovalRequestId?: string;
  inspectionRequired?: boolean;
}

export interface InvoiceArtifactRequest {
  format?: InvoicePrintFormat;
  printerId?: string;
  channel: "PRINT" | "DOWNLOAD" | "EMAIL" | "SHARE";
  reason: string;
  email?: string;
  copies?: number;
  cutPaper?: boolean;
  openDrawer?: boolean;
}

export type PrinterDeliveryJobStatus =
  | "QUEUED"
  | "RENDERED"
  | "SENDING"
  | "SENT"
  | "DIALOG_OPENED"
  | "CONFIRMED_PRINTED"
  | "CANCELLED"
  | "FAILED";

export interface PrinterJobResponse extends JsonObject {
  id: string;
  artifactId: string;
  printerId: string | null;
  sourceType: string;
  sourceId: string;
  format: InvoicePrintFormat;
  channel: "SYSTEM" | "NETWORK" | "SERIAL" | "USB";
  status: PrinterDeliveryJobStatus;
  attempts: number;
  copies: number;
  cutPaper: boolean;
  openDrawer: boolean;
  failureCode: string | null;
  failureMessage: string | null;
  templateVersion: string;
  templateHash: string;
}

export interface TransitionPrinterJobRequest {
  status:
    | "SENDING"
    | "SENT"
    | "DIALOG_OPENED"
    | "CONFIRMED_PRINTED"
    | "CANCELLED"
    | "FAILED";
  failureCode?: string;
  failureMessage?: string;
}

export interface SaleReturnResponse extends JsonObject {
  returnId: string;
  returnNumber: string;
  originalSaleId: string;
  refundPaise: number;
  creditPaise: number;
  acceptedAt: string;
  replayed: boolean;
}

export interface SimpleGoodsReceiptLineRequest {
  purchaseOrderItemId?: string;
  productId: string;
  purchasePackId?: string;
  batchNumber: string;
  expiryDate: string;
  quantity: number;
  freeQuantity?: number;
  cost: number;
  mrp: number;
  gstPercent: number;
}

export interface CompleteSimpleGoodsReceiptRequest {
  supplierId: string;
  purchaseOrderId?: string;
  invoiceNumber: string;
  invoiceDate: string;
  receivedAt?: string;
  directPurchase?: boolean;
  emergencyPurchase?: boolean;
  directPurchaseReason?: string;
  approvalRequestId?: string;
  discountAmount?: number;
  freightAmount?: number;
  handlingAmount?: number;
  insuranceAmount?: number;
  otherCharges?: number;
  tcsAmount?: number;
  roundingAmount?: number;
  items: SimpleGoodsReceiptLineRequest[];
}

export interface CompleteGoodsReceiptResponse extends JsonObject {
  goodsReceiptId: string;
  purchaseInvoiceId?: string;
  grnNumber: string;
  status: "COMPLETED";
  supplierName: string;
  invoiceNumber: string;
  lineCount: number;
  subtotal: string;
  taxAmount: string;
  totalAmount: string;
  replayed: boolean;
}

export interface DirectPurchaseAuthorizationResponse extends JsonObject {
  required: boolean;
  approvalRequestId?: string;
  policy: JsonObject;
}

export interface PendingGoodsReceipt {
  goodsReceiptId: string;
  grnNumber: string;
  receivedAt: string;
  supplierId: string;
  supplierName: string;
  invoiceNumber: string | null;
  lineCount: number;
  totalAmount: string;
  status: "DRAFT";
}

export interface PendingGoodsReceiptList {
  items: PendingGoodsReceipt[];
  total: number;
  limited: boolean;
}

export interface GoodsReceiptPreparer {
  userId: string;
  displayName: string;
  email: string | null;
}

export interface GoodsReceiptReviewLine {
  lineId: string;
  productId: string;
  productName: string;
  sku: string | null;
  barcode: string | null;
  batchNumber: string;
  manufacturedAt: string | null;
  expiryDate: string;
  expectedQuantity: string;
  freeQuantity: string;
  receivedQuantity: string;
  acceptedQuantity: string;
  rejectedQuantity: string;
  varianceQuantity: string;
  rejectionReason: string | null;
  unitPrice: string;
  mrp: string;
  discountPercent: string;
  discountAmount: string;
  taxPercent: string;
  taxAmount: string;
  totalAmount: string;
  packDescription: string | null;
  unitsPerPack: number;
}

export interface PendingGoodsReceiptReview {
  goodsReceiptId: string;
  grnNumber: string;
  receivedAt: string;
  supplierId: string;
  supplierName: string;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  purchaseOrderId: string | null;
  purchaseOrderNumber: string | null;
  reviewHash: string;
  preparedBy: GoodsReceiptPreparer;
  canApprove: boolean;
  subtotal: string;
  discountAmount: string;
  taxAmount: string;
  freightAmount: string;
  handlingAmount: string;
  otherCharges: string;
  totalAmount: string;
  notes: string | null;
  status: "DRAFT";
  lines: GoodsReceiptReviewLine[];
}

export interface ApproveGoodsReceiptRequest {
  reviewHash: string;
}

export interface InventoryBalance extends JsonObject {
  productId: string;
  lotId: string;
  quantity: string;
  balanceVersion: number;
}

export type InventoryStockStatus = "IN_STOCK" | "LOW" | "OUT_OF_STOCK";

export interface InventoryLotView {
  lotId: string;
  batchNumber: string;
  expiryDate: string | null;
  available: string;
  reserved: string;
  damaged: string;
  mrpPaise: string;
  costPaise: string;
  status: string;
}

export interface InventoryStockItem {
  productId: string;
  name: string;
  sku: string;
  barcode: string | null;
  manufacturer: string | null;
  category: string | null;
  unit: string;
  reorderLevel: string | null;
  available: string;
  reserved: string;
  damaged: string;
  stockValuePaise: string;
  nextExpiryDate: string | null;
  status: InventoryStockStatus;
  lots: InventoryLotView[];
}

export interface InventorySummary {
  totalProducts: number;
  inStockProducts: number;
  lowStockProducts: number;
  outOfStockProducts: number;
  totalAvailable: string;
  totalStockValuePaise: string;
  expiringLots: number;
  expiredLots: number;
}

export interface InventorySearchQuery {
  search?: string;
  category?: string;
  status?: "ALL" | InventoryStockStatus;
  sortBy?: "name" | "sku" | "category" | "available" | "stockValue" | "expiry";
  sortOrder?: "asc" | "desc";
  page?: number;
  limit?: number;
  through?: string;
}

export interface InventorySearchResponse {
  items: InventoryStockItem[];
  summary: InventorySummary;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface FefoAllocation extends JsonObject {
  lotId: string;
  quantity: string;
}

export interface ExpiringInventoryQuery {
  through: string;
}

export interface PostStockCountRequest {
  lotId: string;
  physicalQuantity: string;
  expectedBalanceVersion: number;
  reason: string;
}

export interface PostStockCountResponse extends JsonObject {
  commandId: string;
  lotId: string;
  priorQuantity: string;
  physicalQuantity: string;
  variance: string;
  replayed: boolean;
}

export interface InventoryCountArea {
  area: string;
  lotCount: number;
  productCount: number;
  expectedQuantity: string;
}

export interface InventoryCountReviewQuery {
  area: string;
}

export interface InventoryCountLot {
  lotId: string;
  productId: string;
  productName: string;
  sku: string;
  unit: string;
  batchNumber: string;
  expiryDate: string | null;
  expectedQuantity: string;
  balanceVersion: number;
  lotStatus: string;
}

export interface InventoryCountReviewResponse {
  area: string;
  lots: InventoryCountLot[];
}

export interface InventoryCountHistoryQuery {
  search?: string;
  page?: number;
  limit?: number;
}

export interface InventoryCountHistoryItem {
  id: string;
  lotId: string;
  productId: string;
  productName: string;
  sku: string;
  unit: string;
  batchNumber: string;
  expiryDate: string | null;
  area: string;
  countedAt: string;
  countedById: string;
  countedByName: string;
  priorQuantity: string;
  physicalQuantity: string;
  variance: string;
  reason: string;
}

export interface InventoryCountHistoryResponse {
  items: InventoryCountHistoryItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

function appendQuery(path: string, query?: RevivalRequest["query"]): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, String(value));
    }
  }
  const encoded = params.toString();
  return encoded ? `${path}?${encoded}` : path;
}

export const revivalFetchTransport: RevivalTransport = async <T,>(
  request: RevivalRequest,
): Promise<T> => {
  const headers = new Headers(request.headers);
  headers.set(
    "Accept",
    request.responseType === "blob" ? "*/*" : "application/json",
  );
  let body: FormData | string | undefined;
  if (request.body instanceof FormData) {
    body = request.body;
  } else if (request.body !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(request.body);
  }
  const abortController = new AbortController();
  const timeout = request.timeoutMs
    ? setTimeout(() => abortController.abort(), request.timeoutMs)
    : undefined;
  try {
    const response = await fetch(appendQuery(request.path, request.query), {
      method: request.method,
      headers,
      body,
      signal: abortController.signal,
    });
    if (!response.ok) {
      const detail = await response.text().catch(() => "");
      let parsed: unknown;
      try {
        parsed = detail ? JSON.parse(detail) : undefined;
      } catch {
        parsed = undefined;
      }
      const stableError = parseStableApiError(parsed);
      if (stableError) throw new StableApiError(stableError);
      throw new Error(
        `Product revival request failed with status ${response.status}${detail ? `: ${detail}` : ""}`,
      );
    }
    if (request.responseType === "blob") return (await response.blob()) as T;
    if (request.responseType === "text") return (await response.text()) as T;
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  } finally {
    if (timeout) clearTimeout(timeout);
  }
};

export class ProductRevivalApiClient {
  constructor(
    private readonly baseUrl = "",
    private readonly transport: RevivalTransport = revivalFetchTransport,
  ) {}

  private url(path: string): string {
    return `${this.baseUrl.replace(/\/$/, "")}${path}`;
  }

  private commandHeaders(
    options: RevivalCommandOptions,
  ): Record<string, string> {
    return {
      "Idempotency-Key": options.idempotencyKey,
      // A retry must preserve its command correlation identity. Callers may
      // supply a wider workflow correlation ID; otherwise the idempotency key
      // is the stable command-scoped fallback.
      "X-Correlation-ID": options.correlationId ?? options.idempotencyKey,
      ...(options.clientVersion
        ? { "X-Client-Version": options.clientVersion }
        : {}),
      ...(options.clientApplication
        ? { "X-Client-App": options.clientApplication }
        : {}),
      ...(options.businessDate
        ? { "X-Business-Date": options.businessDate }
        : {}),
      ...(options.sourceDevice
        ? { "X-Source-Device": options.sourceDevice }
        : {}),
      ...(options.periodTreatment
        ? { "X-Period-Treatment": options.periodTreatment }
        : {}),
      ...(options.periodReason
        ? { "X-Period-Reason": options.periodReason }
        : {}),
    };
  }

  getHealth(): Promise<RevivalHealthResponse> {
    return this.transport({ path: this.url("/health"), method: "GET" });
  }

  listImportProfiles(): Promise<ImportProfileRecord[]> {
    return this.transport({
      path: this.url("/product-revival/imports/profiles"),
      method: "GET",
    });
  }

  saveImportProfile(
    body: SaveImportProfileRequest,
    options: RevivalCommandOptions,
  ): Promise<ImportProfileRecord> {
    return this.transport({
      path: this.url("/product-revival/imports/profiles"),
      method: "POST",
      body,
      headers: this.commandHeaders(options),
    });
  }

  uploadImportFile(
    file: Blob,
    profileId: string | undefined,
    options: RevivalCommandOptions,
  ): Promise<ImportJobRecord> {
    const body = new FormData();
    body.append("file", file);
    if (profileId) body.append("profileId", profileId);
    return this.transport({
      path: this.url("/product-revival/imports/files"),
      method: "POST",
      body,
      headers: this.commandHeaders(options),
      timeoutMs: 120_000,
    });
  }

  getImportJob(jobId: string): Promise<ImportJobRecord> {
    return this.transport({
      path: this.url(
        `/product-revival/imports/jobs/${encodeURIComponent(jobId)}`,
      ),
      method: "GET",
    });
  }

  parseImportJob(
    jobId: string,
    options: RevivalCommandOptions,
  ): Promise<ImportJobRecord> {
    return this.transport({
      path: this.url(
        `/product-revival/imports/jobs/${encodeURIComponent(jobId)}/parse`,
      ),
      method: "POST",
      headers: this.commandHeaders(options),
      // Parsing and atomically staging large workbooks can exceed the shared
      // Axios client's 30-second default. Keep this slightly above the API's
      // five-minute command timeout so the client receives the final result.
      timeoutMs: 6 * 60_000,
    });
  }

  revalidateImportJob(
    jobId: string,
    options: RevivalCommandOptions,
  ): Promise<ImportJobRecord> {
    return this.transport({
      path: this.url(
        `/product-revival/imports/jobs/${encodeURIComponent(jobId)}/revalidate`,
      ),
      method: "POST",
      headers: this.commandHeaders(options),
      timeoutMs: 6 * 60_000,
    });
  }

  inferImportMapping(jobId: string): Promise<MappingInference> {
    return this.transport({
      path: this.url(
        `/product-revival/imports/jobs/${encodeURIComponent(jobId)}/mapping`,
      ),
      method: "GET",
    });
  }

  applyImportProfile(
    jobId: string,
    body: ApplyImportProfileRequest,
    options: RevivalCommandOptions,
  ): Promise<ImportJobRecord> {
    return this.transport({
      path: this.url(
        `/product-revival/imports/jobs/${encodeURIComponent(jobId)}/profile`,
      ),
      method: "POST",
      body,
      headers: this.commandHeaders(options),
    });
  }

  getImportPreview(jobId: string): Promise<ImportPreview> {
    return this.transport({
      path: this.url(
        `/product-revival/imports/jobs/${encodeURIComponent(jobId)}/preview`,
      ),
      method: "GET",
    });
  }

  applyImportDecisions(
    jobId: string,
    body: ApplyImportDecisionsRequest,
    options: RevivalCommandOptions,
  ): Promise<ImportPreview> {
    return this.transport({
      path: this.url(
        `/product-revival/imports/jobs/${encodeURIComponent(jobId)}/decisions`,
      ),
      method: "POST",
      body,
      headers: this.commandHeaders(options),
    });
  }

  correctImportRow(
    jobId: string,
    rowId: string,
    body: CorrectImportRowRequest,
    options: RevivalCommandOptions,
  ): Promise<ImportRowRecord> {
    return this.transport({
      path: this.url(
        `/product-revival/imports/jobs/${encodeURIComponent(jobId)}/rows/${encodeURIComponent(rowId)}/correction`,
      ),
      method: "POST",
      body,
      headers: this.commandHeaders(options),
    });
  }

  reconcileImport(
    jobId: string,
    body: ReconcileImportRequest,
    options: RevivalCommandOptions,
  ): Promise<ImportReconciliation> {
    return this.transport({
      path: this.url(
        `/product-revival/imports/jobs/${encodeURIComponent(jobId)}/reconciliation`,
      ),
      method: "POST",
      body,
      headers: this.commandHeaders(options),
    });
  }

  getImportReconciliation(jobId: string): Promise<ImportReconciliation> {
    return this.transport({
      path: this.url(
        `/product-revival/imports/jobs/${encodeURIComponent(jobId)}/reconciliation`,
      ),
      method: "GET",
    });
  }

  commitImport(
    jobId: string,
    options: RevivalCommandOptions,
  ): Promise<ImportCommandResult> {
    return this.transport({
      path: this.url(
        `/product-revival/imports/jobs/${encodeURIComponent(jobId)}/commit`,
      ),
      method: "POST",
      body: {},
      headers: this.commandHeaders(options),
      // The API activates the complete reconciled import atomically. Large
      // opening-stock files legitimately take longer than the default API
      // client timeout while that transaction is still progressing.
      timeoutMs: 16 * 60_000,
    });
  }

  undoImport(
    jobId: string,
    body: UndoImportRequest,
    options: RevivalCommandOptions,
  ): Promise<ImportCommandResult> {
    return this.transport({
      path: this.url(
        `/product-revival/imports/jobs/${encodeURIComponent(jobId)}/undo`,
      ),
      method: "POST",
      body,
      headers: this.commandHeaders(options),
    });
  }

  listImportErrors(
    jobId: string,
    query?: ImportErrorFilters,
  ): Promise<ImportErrorRecord[]> {
    return this.transport({
      path: this.url(
        `/product-revival/imports/jobs/${encodeURIComponent(jobId)}/errors`,
      ),
      method: "GET",
      query: {
        code: query?.code,
        field: query?.field,
        severity: query?.severity,
        sourceRow: query?.sourceRow,
      },
    });
  }

  downloadImportErrors(jobId: string): Promise<Blob> {
    return this.transport({
      path: this.url(
        `/product-revival/imports/jobs/${encodeURIComponent(jobId)}/errors.csv`,
      ),
      method: "GET",
      responseType: "blob",
    });
  }

  downloadImportReconciliation(jobId: string): Promise<Blob> {
    return this.transport({
      path: this.url(
        `/product-revival/imports/jobs/${encodeURIComponent(jobId)}/reconciliation.csv`,
      ),
      method: "GET",
      responseType: "blob",
    });
  }

  getOnboardingStatus(): Promise<OnboardingStatus> {
    return this.transport({
      path: this.url("/onboarding/status"),
      method: "GET",
    });
  }

  dismissOnboarding(): Promise<OnboardingStatus> {
    return this.transport({
      path: this.url("/onboarding/dismiss"),
      method: "POST",
    });
  }

  resetOnboarding(): Promise<OnboardingStatus> {
    return this.transport({
      path: this.url("/onboarding/reset"),
      method: "POST",
    });
  }

  getTenantReadiness(
    tenantId: string,
    storeId?: string,
  ): Promise<ReadinessProgress> {
    return this.transport({
      path: this.url(
        `/admin/tenants/${encodeURIComponent(tenantId)}/readiness`,
      ),
      method: "GET",
      query: { storeId },
    });
  }

  createImportSupportAccess(
    body: CreateImportSupportAccessRequest,
  ): Promise<ImportSupportAccess> {
    return this.transport({
      path: this.url("/admin/import-support/access"),
      method: "POST",
      body,
    });
  }

  getImportSupportAccess(accessId: string): Promise<ImportSupportAccess> {
    return this.transport({
      path: this.url(
        `/admin/import-support/access/${encodeURIComponent(accessId)}`,
      ),
      method: "GET",
    });
  }

  endImportSupportAccess(accessId: string): Promise<ImportSupportAccess> {
    return this.transport({
      path: this.url(
        `/admin/import-support/access/${encodeURIComponent(accessId)}/end`,
      ),
      method: "POST",
    });
  }

  listImportSupportJobs(
    query: ImportSupportJobsQuery,
  ): Promise<ImportSupportJobsResponse> {
    return this.transport({
      path: this.url("/admin/import-support/jobs"),
      method: "GET",
      query: {
        accessId: query.accessId,
        status: query.status,
        jobId: query.jobId,
      },
    });
  }

  getImportSupportJob(
    accessId: string,
    jobId: string,
  ): Promise<ImportSupportJobDetails> {
    return this.transport({
      path: this.url(`/admin/import-support/jobs/${encodeURIComponent(jobId)}`),
      method: "GET",
      query: { accessId },
    });
  }

  retryImportSupportJob(
    jobId: string,
    body: RetryImportSupportJobRequest,
    options: RevivalCommandOptions,
  ): Promise<RetryImportSupportJobResponse> {
    return this.transport({
      path: this.url(
        `/admin/import-support/jobs/${encodeURIComponent(jobId)}/retry`,
      ),
      method: "POST",
      body,
      headers: this.commandHeaders(options),
    });
  }

  createSaleQuote(
    body: CreateSaleQuoteRequest,
    options: RevivalCommandOptions,
  ): Promise<SaleQuoteResponse> {
    return this.transport({
      path: this.url("/product-revival/sales/quotes"),
      method: "POST",
      body,
      headers: this.commandHeaders(options),
    });
  }

  requoteSaleQuote(
    quoteId: string,
    body: RequoteSaleQuoteRequest,
    options: RevivalCommandOptions,
  ): Promise<SaleQuoteResponse> {
    return this.transport({
      path: this.url(
        `/product-revival/sales/quotes/${encodeURIComponent(quoteId)}/requote`,
      ),
      method: "POST",
      body,
      headers: this.commandHeaders(options),
    });
  }

  cancelSaleQuote(
    quoteId: string,
    body: CancelSaleQuoteRequest,
    options: RevivalCommandOptions,
  ): Promise<JsonObject> {
    return this.transport({
      path: this.url(
        `/product-revival/sales/quotes/${encodeURIComponent(quoteId)}/cancel`,
      ),
      method: "POST",
      body,
      headers: this.commandHeaders(options),
    });
  }

  listSaleWorkflows(query: SaleWorkflowQuery = {}): Promise<JsonObject> {
    return this.transport({
      path: this.url("/product-revival/sales/workflows"),
      method: "GET",
      query: {
        kind: query.kind,
        state: query.state,
        externalIdentity: query.externalIdentity,
      },
    });
  }

  getSaleWorkflow(workflowId: string): Promise<JsonObject> {
    return this.transport({
      path: this.url(
        `/product-revival/sales/workflows/${encodeURIComponent(workflowId)}`,
      ),
      method: "GET",
    });
  }

  createSaleWorkflow(
    body: SaleWorkflowRequest,
    options: RevivalCommandOptions,
  ): Promise<JsonObject> {
    return this.transport({
      path: this.url("/product-revival/sales/workflows"),
      method: "POST",
      body,
      headers: this.commandHeaders(options),
    });
  }

  transitionSaleWorkflow(
    workflowId: string,
    body: SaleWorkflowTransitionRequest,
    options: RevivalCommandOptions,
  ): Promise<JsonObject> {
    return this.transport({
      path: this.url(
        `/product-revival/sales/workflows/${encodeURIComponent(workflowId)}/transitions`,
      ),
      method: "POST",
      body,
      headers: this.commandHeaders(options),
    });
  }

  editSaleCart(
    workflowId: string,
    body: SaleCartEditRequest,
    options: RevivalCommandOptions,
  ): Promise<JsonObject> {
    return this.transport({
      path: this.url(
        `/product-revival/sales/workflows/${encodeURIComponent(workflowId)}/cart-edits`,
      ),
      method: "POST",
      body,
      headers: this.commandHeaders(options),
    });
  }

  createCanonicalPaymentAttempt(
    body: CreateCanonicalPaymentAttemptRequest,
    options: RevivalCommandOptions,
  ): Promise<CanonicalPaymentAttemptResponse> {
    return this.transport({
      path: this.url("/payment-operations/attempts"),
      method: "POST",
      body,
      headers: this.commandHeaders(options),
    });
  }

  getCanonicalPaymentAttempt(
    attemptId: string,
  ): Promise<CanonicalPaymentAttemptResponse> {
    return this.transport({
      path: this.url(
        `/payment-operations/attempts/${encodeURIComponent(attemptId)}`,
      ),
      method: "GET",
    });
  }

  acceptSale(
    body: AcceptSaleRequest,
    options: RevivalCommandOptions,
  ): Promise<AcceptSaleResponse> {
    return this.transport({
      path: this.url("/product-revival/sales/accept"),
      method: "POST",
      body,
      headers: this.commandHeaders(options),
    });
  }

  pullOfflineSnapshot(
    query: OfflineSnapshotQuery = {},
  ): Promise<OfflineSnapshotResponse> {
    return this.transport({
      path: this.url("/product-revival/offline/snapshot"),
      method: "GET",
      query: { cursor: query.cursor, pageSize: query.pageSize },
    });
  }

  acceptOfflineSale(
    body: AcceptOfflineSaleRequest,
    options: RevivalCommandOptions,
  ): Promise<AcceptSaleResponse> {
    return this.transport({
      path: this.url("/product-revival/offline/sales"),
      method: "POST",
      body,
      headers: this.commandHeaders(options),
      timeoutMs: 30_000,
    });
  }

  getOfflineSaleResolution(clientCommandId: string): Promise<JsonObject> {
    return this.transport({
      path: this.url(
        `/product-revival/offline/sales/${encodeURIComponent(clientCommandId)}/resolution`,
      ),
      method: "GET",
    });
  }

  resolveOfflineSale(
    clientCommandId: string,
    body: OfflineSaleResolutionRequest,
    options: RevivalCommandOptions,
  ): Promise<JsonObject> {
    return this.transport({
      path: this.url(
        `/product-revival/offline/sales/${encodeURIComponent(clientCommandId)}/resolution`,
      ),
      method: "POST",
      body,
      headers: this.commandHeaders(options),
    });
  }

  getCounterSessionWorkspace(): Promise<CounterSessionWorkspace> {
    return this.transport({
      path: this.url("/product-revival/counter/session"),
      method: "GET",
    });
  }

  openCounterSession(
    body: OpenCounterSessionRequest,
    options: RevivalCommandOptions,
  ): Promise<OpenCounterSessionResponse> {
    return this.transport({
      path: this.url("/product-revival/counter/session/open"),
      method: "POST",
      body,
      headers: this.commandHeaders(options),
    });
  }

  getCounterSessionClosePreview(
    sessionId: string,
  ): Promise<CounterSessionClosePreview> {
    return this.transport({
      path: this.url(
        `/product-revival/counter/session/${encodeURIComponent(sessionId)}/close-preview`,
      ),
      method: "GET",
    });
  }

  closeCounterSession(
    sessionId: string,
    body: CloseCounterSessionRequest,
    options: RevivalCommandOptions,
  ): Promise<CloseCounterSessionResponse> {
    return this.transport({
      path: this.url(
        `/product-revival/counter/session/${encodeURIComponent(sessionId)}/close`,
      ),
      method: "POST",
      body,
      headers: this.commandHeaders(options),
    });
  }

  confirmPrinterTest(
    printerId: string,
    body: ConfirmPrinterTestRequest,
    options: RevivalCommandOptions,
  ): Promise<PrinterTestResponse> {
    return this.transport({
      path: this.url(
        `/product-revival/printers/${encodeURIComponent(printerId)}/tests`,
      ),
      method: "POST",
      body,
      headers: this.commandHeaders(options),
    });
  }

  getLastInvoiceForPrint(
    format: InvoicePrintFormat = "80MM",
    printerId?: string,
  ): Promise<InvoicePrintResponse> {
    return this.transport({
      path: this.url("/product-revival/printing/invoices/last"),
      method: "GET",
      query: { format, printerId },
    });
  }

  getInvoiceForPrint(
    saleId: string,
    format: InvoicePrintFormat = "80MM",
    printerId?: string,
  ): Promise<InvoicePrintResponse> {
    return this.transport({
      path: this.url(
        `/product-revival/printing/invoices/${encodeURIComponent(saleId)}`,
      ),
      method: "GET",
      query: { format, printerId },
    });
  }

  getSaleReceiptForPrint(
    saleId: string,
    format: InvoicePrintFormat = "80MM",
    printerId?: string,
  ): Promise<InvoicePrintResponse> {
    return this.transport({
      path: this.url(
        `/product-revival/printing/receipts/${encodeURIComponent(saleId)}`,
      ),
      method: "GET",
      query: { format, printerId },
    });
  }

  createInvoiceArtifact(
    saleId: string,
    body: InvoiceArtifactRequest,
    options: RevivalCommandOptions,
  ): Promise<JsonObject> {
    return this.transport({
      path: this.url(
        `/product-revival/printing/invoices/${encodeURIComponent(saleId)}/artifacts`,
      ),
      method: "POST",
      body,
      headers: this.commandHeaders(options),
    });
  }

  downloadInvoiceArtifact(artifactId: string): Promise<Blob> {
    return this.transport({
      path: this.url(
        `/product-revival/printing/artifacts/${encodeURIComponent(artifactId)}/download`,
      ),
      method: "GET",
      responseType: "blob",
    });
  }

  getPrinterJob(jobId: string): Promise<PrinterJobResponse> {
    return this.transport({
      path: this.url(
        `/product-revival/printing/jobs/${encodeURIComponent(jobId)}`,
      ),
      method: "GET",
    });
  }

  transitionPrinterJob(
    jobId: string,
    body: TransitionPrinterJobRequest,
    options: RevivalCommandOptions,
  ): Promise<PrinterJobResponse> {
    return this.transport({
      path: this.url(
        `/product-revival/printing/jobs/${encodeURIComponent(jobId)}/transitions`,
      ),
      method: "POST",
      body,
      headers: this.commandHeaders(options),
    });
  }

  getReturnableSale(
    invoiceNumber: string,
    options: { includeMigrated?: boolean; includeTenantStores?: boolean } = {},
  ): Promise<ReturnableSaleResponse> {
    return this.transport({
      path: this.url("/product-revival/sales/returnable"),
      method: "GET",
      query: {
        invoiceNumber,
        includeMigrated: options.includeMigrated ?? true,
        includeTenantStores: options.includeTenantStores,
      },
    });
  }

  createSaleReturn(
    body: CreateSaleReturnRequest,
    options: RevivalCommandOptions,
  ): Promise<SaleReturnResponse> {
    return this.transport({
      path: this.url("/product-revival/sales/returns"),
      method: "POST",
      body,
      headers: this.commandHeaders(options),
    });
  }

  createSaleExchange(
    body: JsonObject,
    options: RevivalCommandOptions,
  ): Promise<JsonObject> {
    return this.transport({
      path: this.url("/product-revival/sales/exchanges"),
      method: "POST",
      body,
      headers: this.commandHeaders(options),
    });
  }

  createSaleReturnArtifact(
    returnId: string,
    body: JsonObject,
    options: RevivalCommandOptions,
  ): Promise<JsonObject> {
    return this.transport({
      path: this.url(
        `/product-revival/sales/returns/${encodeURIComponent(returnId)}/artifacts`,
      ),
      method: "POST",
      body,
      headers: this.commandHeaders(options),
    });
  }

  downloadSaleReturnArtifact(artifactId: string): Promise<Blob> {
    return this.transport({
      path: this.url(
        `/product-revival/printing/return-artifacts/${encodeURIComponent(artifactId)}/download`,
      ),
      method: "GET",
      responseType: "blob",
    });
  }

  cancelPostedSale(
    saleId: string,
    body: CancelPostedSaleRequest,
    options: RevivalCommandOptions,
  ): Promise<SaleReturnResponse> {
    return this.transport({
      path: this.url(
        `/product-revival/sales/${encodeURIComponent(saleId)}/cancel`,
      ),
      method: "POST",
      body,
      headers: this.commandHeaders(options),
    });
  }

  lookupSales(query: SalesLookupQuery = {}): Promise<JsonObject> {
    return this.transport({
      path: this.url("/product-revival/sales/lookup"),
      method: "GET",
      query: {
        invoiceNumber: query.invoiceNumber,
        customerId: query.customerId,
        patientId: query.patientId,
        productId: query.productId,
        batchNumber: query.batchNumber,
        tenderMethod: query.tenderMethod,
        prescriptionId: query.prescriptionId,
        barcodeOrQr: query.barcodeOrQr,
        from: query.from,
        to: query.to,
        network: query.network,
        page: query.page,
        limit: query.limit,
      },
    });
  }

  completeSimpleGoodsReceipt(
    body: CompleteSimpleGoodsReceiptRequest,
    options: RevivalCommandOptions,
  ): Promise<CompleteGoodsReceiptResponse> {
    return this.transport({
      path: this.url("/product-revival/goods-receipts/complete"),
      method: "POST",
      body,
      headers: this.commandHeaders(options),
    });
  }

  authorizeDirectPurchase(
    body: CompleteSimpleGoodsReceiptRequest,
    options: RevivalCommandOptions,
  ): Promise<DirectPurchaseAuthorizationResponse> {
    return this.transport({
      path: this.url(
        "/product-revival/goods-receipts/direct-purchase-authorizations",
      ),
      method: "POST",
      body,
      headers: this.commandHeaders(options),
    });
  }

  listPendingGoodsReceipts(): Promise<PendingGoodsReceiptList> {
    return this.transport({
      path: this.url("/product-revival/goods-receipts/pending"),
      method: "GET",
    });
  }

  getPendingGoodsReceiptReview(
    goodsReceiptId: string,
  ): Promise<PendingGoodsReceiptReview> {
    return this.transport({
      path: this.url(
        `/product-revival/goods-receipts/${encodeURIComponent(goodsReceiptId)}/review`,
      ),
      method: "GET",
    });
  }

  approveGoodsReceipt(
    goodsReceiptId: string,
    reviewHash: string,
    options: RevivalCommandOptions,
  ): Promise<CompleteGoodsReceiptResponse> {
    return this.transport({
      path: this.url(
        `/product-revival/goods-receipts/${encodeURIComponent(goodsReceiptId)}/complete`,
      ),
      method: "POST",
      body: { reviewHash },
      headers: this.commandHeaders(options),
    });
  }

  getInventoryBalances(productId?: string): Promise<InventoryBalance[]> {
    return this.transport({
      path: this.url("/product-revival/inventory/balances"),
      method: "GET",
      query: { productId },
    });
  }

  searchInventory(
    query: InventorySearchQuery = {},
  ): Promise<InventorySearchResponse> {
    return this.transport({
      path: this.url("/product-revival/inventory/search"),
      method: "GET",
      query: {
        search: query.search,
        category: query.category,
        status: query.status,
        sortBy: query.sortBy,
        sortOrder: query.sortOrder,
        page: query.page,
        limit: query.limit,
        through: query.through,
      },
    });
  }

  getFefoAllocations(
    productId: string,
    quantity: string,
  ): Promise<FefoAllocation[]> {
    return this.transport({
      path: this.url("/product-revival/inventory/fefo"),
      method: "GET",
      query: { productId, quantity },
    });
  }

  getLowStock(): Promise<InventoryBalance[]> {
    return this.transport({
      path: this.url("/product-revival/inventory/low-stock"),
      method: "GET",
    });
  }

  getExpiringInventory(through: string): Promise<InventoryBalance[]> {
    return this.transport({
      path: this.url("/product-revival/inventory/expiring"),
      method: "GET",
      query: { through },
    });
  }

  postStockCount(
    body: PostStockCountRequest,
    options: RevivalCommandOptions,
  ): Promise<PostStockCountResponse> {
    return this.transport({
      path: this.url("/product-revival/inventory/counts"),
      method: "POST",
      body,
      headers: this.commandHeaders(options),
    });
  }

  getInventoryCountAreas(): Promise<InventoryCountArea[]> {
    return this.transport({
      path: this.url("/product-revival/inventory/counts/areas"),
      method: "GET",
    });
  }

  getInventoryCountHistory(
    query: InventoryCountHistoryQuery = {},
  ): Promise<InventoryCountHistoryResponse> {
    return this.transport({
      path: this.url("/product-revival/inventory/counts/history"),
      method: "GET",
      query: {
        search: query.search,
        page: query.page,
        limit: query.limit,
      },
    });
  }

  getInventoryCountReview(area: string): Promise<InventoryCountReviewResponse> {
    return this.transport({
      path: this.url("/product-revival/inventory/counts/review"),
      method: "GET",
      query: { area },
    });
  }
}
